import React, { useCallback, useEffect, useState } from 'react';
import {
    CreditCard,
    AlertCircle,
    Send,
    Link2,
    RefreshCw,
    Pencil,
    CheckCircle2,
    Copy,
    Undo2,
    X,
} from 'lucide-react';
import { paymentService } from '../../services/payment.service';
import { runInLane } from '../../utils/requestLane';
import { addCaseNote } from '../../services/addService.service';
import { formatDateTime } from '../../utils/dateUtils';
import { formatMoney, setMoneyDefaults, getDefaultCurrency } from '../../utils/formatMoney';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import UpdatePaymentModal from './UpdatePaymentModal';

const STATUS_BADGE = {
    succeeded: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
    refunded: 'bg-blue-100 text-blue-800',
};

function refundSourceLabel(source) {
    const s = String(source || '').toLowerCase();
    if (s === 'cancellation_settlement') return 'Cancellation settlement';
    if (s === 'dispute_fee_waiver') return 'Dispute fee waiver';
    if (s === 'admin') return 'Admin refund';
    return source || 'Refund';
}

function disputeRefundTypeLabel(type) {
    const t = String(type || '').toLowerCase();
    if (t === 'fee_waiver') return 'Dispute fee waiver';
    if (t === 'full') return 'Dispute full refund';
    return 'Dispute refund';
}

const CustomerPaymentPanel = ({
    serviceId,
    caseId,
    serviceNumber,
    serviceStatus,
    customerMobile,
    customerName,
    estimatedAmount,
    vendorAssigned = false,
    isPaidCustomer = false,
    onPaymentUpdated,
    cancellationCharges: cancellationChargesProp = null,
}) => {
    const { isAdmin, isManager } = useRoleAccess();
    const canIssueRefund = isAdmin || isManager;
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [payment, setPayment] = useState(null);
    const [cancellationCharges, setCancellationCharges] = useState(cancellationChargesProp);
    const [disputeRefunds, setDisputeRefunds] = useState([]);
    const [context, setContext] = useState({
        estimated_amount: estimatedAmount,
        service_status: serviceStatus,
        vendor_assigned: vendorAssigned,
        can_send_payment_link: false,
        retry: null,
    });
    const [showManualModal, setShowManualModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundReason, setRefundReason] = useState('');
    const [refundConfirm, setRefundConfirm] = useState(false);
    const [sendWhatsApp, setSendWhatsApp] = useState(true);
    const [mobileOverride, setMobileOverride] = useState(customerMobile || '');

    const [providerReady, setProviderReady] = useState(null);
    const [providerName, setProviderName] = useState('');

    const loadPayment = useCallback(async () => {
        if (!serviceId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await runInLane(() => paymentService.getServicePayment(serviceId));
            setPayment(response?.payment ?? null);
            setCancellationCharges(
                response?.cancellation_charges ?? cancellationChargesProp ?? null,
            );
            setDisputeRefunds(
                Array.isArray(response?.dispute_refunds) ? response.dispute_refunds : [],
            );
            setContext({
                estimated_amount: response?.estimated_amount ?? estimatedAmount,
                service_status: response?.service_status ?? serviceStatus,
                vendor_assigned: response?.vendor_assigned ?? vendorAssigned,
                can_send_payment_link: Boolean(response?.can_send_payment_link),
                retry: response?.retry || response?.payment?.retry || null,
            });
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to load payment');
        } finally {
            setLoading(false);
        }
    }, [serviceId, estimatedAmount, serviceStatus, vendorAssigned, cancellationChargesProp]);

    useEffect(() => {
        loadPayment();
    }, [loadPayment]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cfg = await paymentService.getPaymentsConfig();
                if (cancelled) return;
                setMoneyDefaults({ currency: cfg?.currency, locale: cfg?.locale, timezone: cfg?.timezone });
                const provider = String(cfg?.provider || '').toLowerCase();
                setProviderName(provider);
                let ready = false;
                if (provider === 'stripe') {
                    ready = Boolean(cfg?.stripe?.publishable_key);
                } else if (provider === 'razorpay') {
                    ready = Boolean(cfg?.razorpay?.enabled && cfg?.razorpay?.key_id);
                }
                setProviderReady(ready);
            } catch {
                if (!cancelled) setProviderReady(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const savePaymentLinkCaseNoteFallback = async (response, phone) => {
        if (!caseId || response?.case_note_id) return;
        const link = response?.payment_link_url;
        if (!link) return;
        const recipient = customerName || 'Customer';
        const amountText =
            response?.estimated_amount != null
                ? formatMoney(response.estimated_amount)
                : '—';
        const noteText = [
            `Payment link for service ${serviceNumber || serviceId} (${amountText})`,
            `sent to ${recipient} (${phone || '—'}) on ${formatDateTime(new Date().toISOString())}.`,
            `Delivery: ${response?.whatsapp_sent ? 'sent via WhatsApp' : 'link created in portal'}.`,
            `Link: ${link}`,
        ].join(' ');
        try {
            await addCaseNote(caseId, {
                note_type: 'payment_link',
                note_text: noteText,
                is_internal: false,
            });
        } catch (noteErr) {
            console.error('Failed to save payment link case note (fallback):', noteErr);
        }
    };

    useEffect(() => {
        setMobileOverride(customerMobile || '');
    }, [customerMobile]);

    // Poll while payment is pending so auto Stripe updates appear without refresh
    useEffect(() => {
        if (!serviceId || payment?.status !== 'pending') return undefined;
        const timer = setInterval(loadPayment, 15000);
        return () => clearInterval(timer);
    }, [serviceId, payment?.status, loadPayment]);

    const handleSendLink = async () => {
        setActionLoading('send');
        setError('');
        setSuccess('');
        try {
            const response = await paymentService.sendPaymentLink(serviceId, {
                mobile_number: mobileOverride?.trim() || undefined,
                send_whatsapp: sendWhatsApp,
            });
            setPayment(response?.payment ?? payment);
            setSuccess(
                response?.whatsapp_sent
                    ? 'Payment link sent via WhatsApp. Details recorded in case notes.'
                    : 'Payment link created. Copy the link below if needed. Details recorded in case notes.',
            );
            if (response?.payment_link_url) {
                setContext((prev) => ({ ...prev, payment_link_url: response.payment_link_url }));
            }
            await savePaymentLinkCaseNoteFallback(response, mobileOverride?.trim() || customerMobile);
            if (!response?.case_note_saved && !response?.case_note_id) {
                setSuccess((prev) => `${prev} Note could not be auto-saved — added via fallback if possible.`);
            }
            if (onPaymentUpdated) onPaymentUpdated(response);
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to send payment link');
        } finally {
            setActionLoading('');
        }
    };

    const handleManualSubmit = async (payload) => {
        const response = await paymentService.recordManualPayment(serviceId, payload);
        setPayment(response?.payment ?? payment);
        if (response?.service_status) {
            setContext((prev) => ({ ...prev, service_status: response.service_status }));
        }
        setSuccess('Payment recorded successfully.');
        if (onPaymentUpdated) onPaymentUpdated(response);
        await loadPayment();
    };

    const openRefundModal = () => {
        setRefundReason(payment?.refund_reason || '');
        setRefundConfirm(false);
        setError('');
        setShowRefundModal(true);
    };

    const handleRefundSubmit = async (event) => {
        event.preventDefault();
        const reason = refundReason.trim();
        if (reason.length < 3) {
            setError('Enter a refund reason.');
            return;
        }
        if (!refundConfirm) {
            setError('Confirm the refund before submitting.');
            return;
        }
        setActionLoading('refund');
        setError('');
        setSuccess('');
        try {
            const response = await paymentService.refundPayment(serviceId, {
                reason,
                confirm: true,
            });
            setPayment(response?.payment ?? payment);
            setShowRefundModal(false);
            setSuccess(
                response?.already_refunded
                    ? 'This payment was already refunded.'
                    : `Refund issued for ${formatMoney(response?.payment?.amount ?? payment?.amount, response?.payment?.currency || payment?.currency)}.`,
            );
            if (onPaymentUpdated) onPaymentUpdated(response);
            await loadPayment();
        } catch (err) {
            setError(err?.response?.data?.detail || err?.message || 'Failed to refund payment');
        } finally {
            setActionLoading('');
        }
    };

    const copyLink = async (url) => {
        if (!url) return;
        const cleaned = String(url).trim().replace(/[.,;)\]]+$/, '');
        try {
            await navigator.clipboard.writeText(cleaned);
            setSuccess('Payment link copied to clipboard.');
        } catch {
            setError('Could not copy link. Please copy it manually.');
        }
    };

    const amountDue = context.estimated_amount ?? estimatedAmount;
    const vendorIsAssigned = context.vendor_assigned ?? vendorAssigned;
    const awaitingPayment = (context.service_status || serviceStatus) === 'awaiting_customer_payment';
    const paymentSucceeded = payment?.status === 'succeeded';
    const canSendLink =
        (context.can_send_payment_link ||
            (awaitingPayment && vendorIsAssigned && Number(amountDue) > 0)) &&
        !paymentSucceeded;

    const HOSTED_LINK_PROVIDERS = ['stripe', 'razorpay'];
    const paymentLinkUrl = payment?.payment_link_url || context.payment_link_url;
    const paymentLinkSent = Boolean(
        paymentLinkUrl ||
        (payment?.status === 'pending' && HOSTED_LINK_PROVIDERS.includes(payment?.provider)) ||
        payment?.status === 'succeeded',
    );
    const providerLabel = providerName === 'razorpay' ? 'Razorpay' : providerName === 'stripe' ? 'Stripe' : 'payment';
    const showUpdatePayment = vendorIsAssigned && paymentLinkSent && !paymentSucceeded;
    const refundableProviders = ['stripe', 'dummy'];
    const canRefund =
        canIssueRefund &&
        paymentSucceeded &&
        refundableProviders.includes(String(payment?.provider || '').toLowerCase());
    const refundProviderLabel = payment?.provider
        ? String(payment.provider).charAt(0).toUpperCase() + String(payment.provider).slice(1)
        : providerLabel;

    if (loading) {
        return (
            <div className="rounded-lg border border-gray-200 bg-white p-6">
                <h3 className="mb-4 flex items-center gap-2 border-b text-lg font-semibold text-black">
                    <CreditCard className="h-5 w-5" /> Customer Payment
                </h3>
                <div className="text-sm text-gray-500">Loading payment status…</div>
            </div>
        );
    }

    return (
        <>
            <div className="rounded-lg border border-gray-200 bg-white p-6">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-4">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-black">
                        <CreditCard className="h-5 w-5" /> Customer Payment
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={loadPayment}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                            disabled={!!actionLoading}
                        >
                            <RefreshCw className="h-4 w-4" /> Refresh
                        </button>
                        {showUpdatePayment ? (
                            <button
                                type="button"
                                onClick={() => setShowManualModal(true)}
                                className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
                            >
                                <Pencil className="h-4 w-4" /> Update Payment Details
                            </button>
                        ) : null}
                        {canRefund ? (
                            <button
                                type="button"
                                onClick={openRefundModal}
                                disabled={!!actionLoading}
                                className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
                            >
                                <Undo2 className="h-4 w-4" /> Refund via {refundProviderLabel}
                            </button>
                        ) : null}
                        {canSendLink ? (
                            <button
                                type="button"
                                onClick={handleSendLink}
                                disabled={actionLoading === 'send'}
                                className="inline-flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
                            >
                                <Send className="h-4 w-4" />
                                {actionLoading === 'send' ? 'Sending…' : 'Send Payment Link'}
                            </button>
                        ) : null}
                    </div>
                </div>

                {error ? (
                    <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                    </div>
                ) : null}

                {success ? (
                    <div className="mb-4 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
                        <CheckCircle2 className="h-4 w-4 shrink-0" /> {success}
                    </div>
                ) : null}

                <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                    <SummaryTile label="Amount Due" value={amountDue != null ? formatMoney(amountDue) : '—'} />
                    <SummaryTile label="Service Status" value={context.service_status || serviceStatus || '—'} />
                    <SummaryTile
                        label="Vendor"
                        value={vendorIsAssigned ? 'Assigned — awaiting payment' : 'Not assigned yet'}
                    />
                    <SummaryTile
                        label="Payment Status"
                        value={payment?.status ? payment.status.replace(/_/g, ' ') : 'Not recorded'}
                        highlight={
                            payment?.status === 'succeeded'
                                ? 'success'
                                : payment?.status === 'pending'
                                  ? 'pending'
                                  : payment?.status === 'refunded'
                                    ? 'refunded'
                                    : null
                        }
                    />
                </div>

                {providerReady === false ? (
                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        The selected payment provider{providerName ? ` (${providerLabel})` : ''} is not configured on
                        the server. Set the provider and its keys in Admin → Configuration → payments.
                    </div>
                ) : null}

                {isPaidCustomer && awaitingPayment && !vendorIsAssigned ? (
                    <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        <strong>Step 1:</strong> Assign a vendor first. The service price is calculated when the vendor is selected.
                    </div>
                ) : null}

                {payment?.status === 'failed' ? (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                        <strong>Customer payment failed.</strong> Ask them to update their payment method in the app.
                        {context.retry?.admin_notified
                            ? ' Admin was notified after repeated failures.'
                            : ''}
                        {context.retry
                            ? ` Attempts: ${context.retry.attempt_count || 0}/${context.retry.max_retries || 2}.`
                            : ''}
                    </div>
                ) : null}

                {isPaidCustomer && awaitingPayment && vendorIsAssigned && !paymentSucceeded ? (
                    <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        <strong>Step 2:</strong> Vendor is assigned and price is {formatMoney(amountDue)}.
                        {paymentLinkSent
                            ? ' Payment link was sent to the customer. Use Update Payment Details to record cash/EFTPOS if needed.'
                            : ` Send the ${providerLabel} payment link to the customer (or it is sent automatically when you assign the vendor).`}
                        {' '}Status moves to <em>assigned</em> after payment.
                    </div>
                ) : null}

                {canSendLink ? (
                    <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-4">
                        <p className="mb-3 text-sm text-gray-700">
                            Send a secure {providerLabel} payment link for the vendor-quoted amount. When the customer pays,
                            the service status updates to <strong>assigned</strong> automatically.
                        </p>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600">Customer mobile</label>
                                <input
                                    type="text"
                                    value={mobileOverride}
                                    onChange={(e) => setMobileOverride(e.target.value)}
                                    placeholder="Customer WhatsApp number"
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                />
                            </div>
                            <div className="flex items-end">
                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={sendWhatsApp}
                                        onChange={(e) => setSendWhatsApp(e.target.checked)}
                                        className="rounded border-gray-300 text-green-600"
                                    />
                                    Also send via WhatsApp
                                </label>
                            </div>
                        </div>
                    </div>
                ) : null}

                {paymentLinkUrl ? (
                    <div className="mb-4 rounded-md border border-blue-100 bg-blue-50 p-3">
                        <div className="mb-1 flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-blue-800">
                            <Link2 className="h-3.5 w-3.5" /> Active payment link
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <a
                                href={paymentLinkUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="break-all text-sm text-blue-700 underline"
                            >
                                {paymentLinkUrl}
                            </a>
                            <button
                                type="button"
                                onClick={() => copyLink(paymentLinkUrl)}
                                className="inline-flex items-center gap-1 rounded border border-blue-200 bg-white px-2 py-1 text-xs text-blue-700 hover:bg-blue-100"
                            >
                                <Copy className="h-3.5 w-3.5" /> Copy
                            </button>
                        </div>
                    </div>
                ) : null}

                {!payment ? (
                    <div className="text-sm text-gray-500">No payment recorded for this service yet.</div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <Field label="Provider" value={payment.provider} />
                        <Field
                            label="Status"
                            valueNode={
                                <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_BADGE[payment.status] || 'bg-gray-100 text-gray-800'}`}>
                                    {payment.status}
                                </span>
                            }
                        />
                        <Field label="Amount" value={formatMoney(payment.amount, payment.currency)} />
                        <Field label="Method" value={payment.payment_method || payment.provider} />
                        <Field label="Reference ID" value={payment.transaction_reference || payment.payment_intent_id || payment.provider_payment_id} mono />
                        <Field label="Created" value={payment.created_at ? formatDateTime(payment.created_at) : '-'} />
                        <Field label="Succeeded At" value={payment.succeeded_at ? formatDateTime(payment.succeeded_at) : '-'} />
                        {payment.refunded_at ? (
                            <Field label="Fully Refunded At" value={formatDateTime(payment.refunded_at)} />
                        ) : null}
                        {(() => {
                            const currency = payment.currency;
                            const refundRows = Array.isArray(payment.refunds) ? payment.refunds.filter((r) => r && typeof r === 'object') : [];
                            const totalFromHistory = refundRows.reduce((sum, r) => {
                                if (String(r.status || '').toLowerCase() === 'failed') return sum;
                                const n = Number(r.amount);
                                return sum + (Number.isFinite(n) ? n : 0);
                            }, 0);
                            const totalRefunded = Number(
                                payment.total_refunded_amount ??
                                    (refundRows.length ? totalFromHistory : payment.refunded_amount),
                            );
                            const hasCancel =
                                cancellationCharges &&
                                typeof cancellationCharges.amount_paid === 'number' &&
                                cancellationCharges.amount_paid > 0;
                            const hasDisputes = Array.isArray(disputeRefunds) && disputeRefunds.length > 0;
                            const hasLedger = refundRows.length > 0 || hasCancel || hasDisputes || Number.isFinite(totalRefunded);

                            if (!hasLedger) return null;

                            return (
                                <div className="md:col-span-2 lg:col-span-3 space-y-4">
                                    {hasCancel ? (
                                        <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
                                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-900">
                                                Cancellation settlement
                                            </div>
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                                <Field
                                                    label="Amount paid"
                                                    value={formatMoney(cancellationCharges.amount_paid, cancellationCharges.currency || currency)}
                                                />
                                                <Field
                                                    label="Cancellation fee retained"
                                                    value={formatMoney(
                                                        cancellationCharges.fee_amount ?? cancellationCharges.amount_retained ?? 0,
                                                        cancellationCharges.currency || currency,
                                                    )}
                                                />
                                                <Field
                                                    label="Refunded at cancel"
                                                    value={formatMoney(
                                                        cancellationCharges.amount_refunded ?? 0,
                                                        cancellationCharges.currency || currency,
                                                    )}
                                                />
                                            </div>
                                            {cancellationCharges.refund_id ? (
                                                <div className="mt-2 text-xs text-gray-600">
                                                    Cancel refund ID:{' '}
                                                    <span className="font-mono">{cancellationCharges.refund_id}</span>
                                                </div>
                                            ) : null}
                                            {cancellationCharges.settlement_status ? (
                                                <div className="mt-1 text-xs text-gray-600">
                                                    Settlement: {cancellationCharges.settlement_status}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    {hasDisputes ? (
                                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-emerald-900">
                                                Dispute refunds
                                            </div>
                                            <div className="space-y-2">
                                                {disputeRefunds.map((d) => (
                                                    <div
                                                        key={`dispute-refund-${d.dispute_id}`}
                                                        className="flex flex-wrap items-baseline justify-between gap-2 rounded border border-emerald-100 bg-white px-3 py-2 text-sm"
                                                    >
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {disputeRefundTypeLabel(d.type)}
                                                            </div>
                                                            {d.resolution_note ? (
                                                                <div className="text-xs text-gray-600">{d.resolution_note}</div>
                                                            ) : null}
                                                            {d.refund_id ? (
                                                                <div className="font-mono text-xs text-gray-500">{d.refund_id}</div>
                                                            ) : null}
                                                        </div>
                                                        <div className="font-semibold text-emerald-800">
                                                            {formatMoney(d.refunded_amount, currency)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : null}

                                    {refundRows.length > 0 ? (
                                        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
                                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-900">
                                                Refund history
                                            </div>
                                            <div className="space-y-2">
                                                {refundRows.map((r, idx) => (
                                                    <div
                                                        key={r.refund_id || `refund-${idx}`}
                                                        className="flex flex-wrap items-baseline justify-between gap-2 rounded border border-blue-100 bg-white px-3 py-2 text-sm"
                                                    >
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {refundSourceLabel(r.source)}
                                                            </div>
                                                            {r.reason ? (
                                                                <div className="text-xs text-gray-600">{r.reason}</div>
                                                            ) : null}
                                                            {r.refund_id ? (
                                                                <div className="font-mono text-xs text-gray-500">{r.refund_id}</div>
                                                            ) : null}
                                                            {r.at ? (
                                                                <div className="text-xs text-gray-500">{formatDateTime(r.at)}</div>
                                                            ) : null}
                                                        </div>
                                                        <div className="font-semibold text-blue-900">
                                                            {formatMoney(r.amount, currency)}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : payment.refund_id || payment.refund_reason || payment.refunded_amount != null ? (
                                        <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
                                            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-900">
                                                Latest payment refund snapshot
                                            </div>
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                {payment.refund_id ? (
                                                    <Field label="Refund ID" value={payment.refund_id} mono />
                                                ) : null}
                                                {payment.refunded_amount != null ? (
                                                    <Field
                                                        label="Recorded amount"
                                                        value={formatMoney(payment.refunded_amount, currency)}
                                                    />
                                                ) : null}
                                            </div>
                                            {payment.refund_reason ? (
                                                <div className="mt-2 rounded border border-blue-200 bg-white p-2 text-sm text-blue-900">
                                                    {payment.refund_reason}
                                                </div>
                                            ) : null}
                                            <p className="mt-2 text-xs text-amber-800">
                                                Older payments may only store the last refund on the payment row.
                                                Use cancellation settlement and dispute refunds above for the full picture.
                                            </p>
                                        </div>
                                    ) : null}

                                    {Number.isFinite(totalRefunded) && totalRefunded > 0 ? (
                                        <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
                                            <span className="text-sm font-semibold text-indigo-900">
                                                Total refunded to customer
                                            </span>
                                            <span className="text-lg font-bold text-indigo-950">
                                                {formatMoney(
                                                    // Prefer reconstructed total when history is incomplete:
                                                    // cancel refund + dispute refunds, else payment total.
                                                    (() => {
                                                        const cancelPart = Number(cancellationCharges?.amount_refunded || 0);
                                                        const disputePart = (disputeRefunds || []).reduce(
                                                            (s, d) => s + Number(d.refunded_amount || 0),
                                                            0,
                                                        );
                                                        const reconstructed = cancelPart + disputePart;
                                                        if (refundRows.length > 0) return totalRefunded;
                                                        if (reconstructed > 0) return reconstructed;
                                                        return totalRefunded;
                                                    })(),
                                                    currency,
                                                )}
                                            </span>
                                        </div>
                                    ) : hasCancel || hasDisputes ? (
                                        <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
                                            <span className="text-sm font-semibold text-indigo-900">
                                                Total refunded to customer
                                            </span>
                                            <span className="text-lg font-bold text-indigo-950">
                                                {formatMoney(
                                                    Number(cancellationCharges?.amount_refunded || 0) +
                                                        (disputeRefunds || []).reduce(
                                                            (s, d) => s + Number(d.refunded_amount || 0),
                                                            0,
                                                        ),
                                                    currency,
                                                )}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })()}
                        {payment.notes ? (
                            <div className="md:col-span-2 lg:col-span-3">
                                <div className="mb-1 text-xs font-medium text-gray-600">Notes</div>
                                <div className="rounded border border-gray-200 bg-gray-50 p-2 text-sm text-gray-800">{payment.notes}</div>
                            </div>
                        ) : null}
                        {payment.failure_reason ? (
                            <div className="md:col-span-2 lg:col-span-3">
                                <div className="mb-1 text-xs font-medium text-gray-600">Failure Reason</div>
                                <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{payment.failure_reason}</div>
                            </div>
                        ) : null}
                        {context.retry ? (
                            <Field
                                label="Failed attempts"
                                value={
                                    `${context.retry.attempt_count || 0} / ${context.retry.max_retries || 2}` +
                                    (context.retry.admin_notified ? ' (admin notified)' : '')
                                }
                            />
                        ) : null}
                    </div>
                )}
            </div>

            <UpdatePaymentModal
                isOpen={showManualModal}
                onClose={() => setShowManualModal(false)}
                onSubmit={handleManualSubmit}
                serviceNumber={serviceNumber}
                defaultAmount={amountDue}
                currency={payment?.currency || getDefaultCurrency()}
                currentStatus={context.service_status || serviceStatus}
                vendorAssigned={vendorIsAssigned}
                existingReference={
                    payment?.transaction_reference || payment?.payment_intent_id || payment?.provider_payment_id || ''
                }
            />

            {showRefundModal && payment ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                    <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b px-6 py-4">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Refund via {refundProviderLabel}
                                </h3>
                                {serviceNumber ? (
                                    <p className="mt-0.5 text-sm text-gray-500">Service {serviceNumber}</p>
                                ) : null}
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowRefundModal(false)}
                                className="rounded bg-red-500 p-1 text-white hover:bg-red-600"
                                aria-label="Close"
                                disabled={actionLoading === 'refund'}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <form onSubmit={handleRefundSubmit} className="space-y-4 p-6">
                            <dl className="grid grid-cols-1 gap-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm sm:grid-cols-2">
                                <div>
                                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Amount</dt>
                                    <dd className="mt-0.5 font-semibold text-gray-900">
                                        {formatMoney(payment.amount, payment.currency)}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Paid at</dt>
                                    <dd className="mt-0.5 text-gray-900">
                                        {payment.succeeded_at ? formatDateTime(payment.succeeded_at) : '—'}
                                    </dd>
                                </div>
                                <div className="sm:col-span-2">
                                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Reference</dt>
                                    <dd className="mt-0.5 break-all font-mono text-gray-900">
                                        {payment.transaction_reference ||
                                            payment.payment_intent_id ||
                                            payment.provider_payment_id ||
                                            '—'}
                                    </dd>
                                </div>
                            </dl>
                            {error ? (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                    {error}
                                </div>
                            ) : null}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                    Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={refundReason}
                                    onChange={(e) => setRefundReason(e.target.value)}
                                    rows={3}
                                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                    minLength={3}
                                    maxLength={500}
                                />
                            </div>
                            <label className="flex items-start gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={refundConfirm}
                                    onChange={(e) => setRefundConfirm(e.target.checked)}
                                    className="mt-0.5 rounded border-gray-300 text-red-600"
                                />
                                Refund {formatMoney(payment.amount, payment.currency)} to the customer through{' '}
                                {refundProviderLabel}. This does not cancel the service.
                            </label>
                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowRefundModal(false)}
                                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                    disabled={actionLoading === 'refund'}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading === 'refund' || !refundConfirm}
                                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
                                >
                                    {actionLoading === 'refund' ? 'Refunding…' : 'Issue refund'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </>
    );
};

const SummaryTile = ({ label, value, highlight }) => {
    const tone =
        highlight === 'success'
            ? 'border-green-200 bg-green-50 text-green-900'
            : highlight === 'pending'
              ? 'border-yellow-200 bg-yellow-50 text-yellow-900'
              : highlight === 'refunded'
                ? 'border-blue-200 bg-blue-50 text-blue-900'
              : 'border-gray-200 bg-white text-gray-900';

    return (
        <div className={`rounded-lg border p-3 ${tone}`}>
            <div className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</div>
            <div className="mt-1 text-sm font-semibold capitalize">{value}</div>
        </div>
    );
};

const Field = ({ label, value, valueNode, mono }) => (
    <div>
        <div className="mb-1 text-xs font-medium text-gray-600">{label}</div>
        {valueNode ? (
            valueNode
        ) : (
            <div className={`text-sm break-all text-gray-900 ${mono ? 'font-mono' : ''}`}>{value || '-'}</div>
        )}
    </div>
);

export default CustomerPaymentPanel;
